import { useMemo } from 'react'

import { keepPreviousData } from '@tanstack/react-query'
import { gql } from 'graphql-tag'
import isNil from 'lodash.isnil'
import omit from 'lodash.omit'
import {
  ArchiveFeeMutation,
  ArchiveFeeMutationVariables,
  ActivateFeeMutation,
  ActivateFeeMutationVariables,
  CreateFeeInput,
  CreateNewFeeMutation,
  CreateNewFeeMutationVariables,
  DuplicateFeeMutation,
  DuplicateFeeMutationVariables,
  FeeType,
  GetFeesPageQuery,
  GetFeesPageQueryVariables,
  GetFeesQuery,
  GetFeesQueryInput,
  GetFeesQueryVariables,
  GetFeeQuery,
  GetFeeQueryVariables,
  GetLateFeeSampleCalculationQuery,
  GetLateFeeSampleCalculationQueryVariables,
  SampleLateFeeCalculatorInput,
  UpdateFeeMutation,
  UpdateFeeMutationVariables,
  GetFeeCalculationExplanationMutation,
  GetFeeCalculationExplanationMutationVariables,
  GetFeeTemplateForConfigQuery,
  GetFeeTemplateForConfigQueryVariables,
  GetFeeTagQuery,
  GetFeeTagQueryVariables,
} from 'types/graphql'

import { useActiveOrgId } from 'src/hooks/use-active-org-id'
import { NullablePartial } from 'src/types'
import { filterNil } from 'src/utils'

import { QueryKey, useMutation, useQuery, UseQueryOptions } from '../utils/queryClient'

const Archive = gql`
  mutation ArchiveFeeMutation($id: Int!, $feeScheduleId: Int!) {
    archiveFee(id: $id, feeScheduleId: $feeScheduleId) {
      id
    }
  }
`
export const useArchiveFeeMutation = () => {
  return useMutation<ArchiveFeeMutation, ArchiveFeeMutationVariables>({
    mutationDocument: Archive,
  })
}

const Activate = gql`
  mutation ActivateFeeMutation($id: Int!, $feeScheduleId: Int!) {
    activateFee(id: $id, feeScheduleId: $feeScheduleId) {
      id
    }
  }
`
export const useActivateFeeMutation = () => {
  return useMutation<ActivateFeeMutation, ActivateFeeMutationVariables>({
    mutationDocument: Activate,
  })
}

const CreateNewFee = gql`
  mutation CreateNewFeeMutation($input: CreateFeeInput!) {
    createFee(input: $input) {
      id
    }
  }
`
export const useCreateNewFeeMutation = () => {
  return useMutation<CreateNewFeeMutation, CreateNewFeeMutationVariables>({
    mutationDocument: CreateNewFee,
  })
}

const UpdateFee = gql`
  mutation UpdateFeeMutation(
    $id: Int!
    $input: CreateFeeInput!
    $updateExistingDraftFees: Boolean
  ) {
    updateFee(id: $id, input: $input, updateExistingDraftFees: $updateExistingDraftFees) {
      id
    }
  }
`
export const useUpdateFeeMutation = () => {
  return useMutation<UpdateFeeMutation, UpdateFeeMutationVariables>({
    mutationDocument: UpdateFee,
  })
}

const Get = gql`
  query GetFeesQuery($organizationId: Int!, $input: GetFeesInput) {
    fees(organizationId: $organizationId, input: $input) {
      ...FeeFragment
    }
  }
`
export const useFeesQuery = (args?: {
  allowedTypes?: FeeType[]
  includeFeeIds?: number[]
  feeScheduleId?: number
}) => {
  const organizationId = useActiveOrgId()
  const variables = {
    organizationId: organizationId ?? -1,
    input: {
      includeFeeIds: args?.includeFeeIds,
      feeScheduleId: args?.feeScheduleId,
    },
  }
  const result = useQuery<GetFeesQuery, GetFeesQueryVariables>({
    queryKey: [QueryKey.Fees, variables],
    queryDocument: Get,
    variables,
    enabled: !!organizationId,
    placeholderData: keepPreviousData,
  })

  const allowedTypesSet: Set<FeeType> | undefined = useMemo(
    () => (args?.allowedTypes ? new Set(args.allowedTypes) : undefined),
    [args?.allowedTypes]
  )
  const feeTemplates = useMemo(
    () =>
      filterNil(result.data?.fees ?? []).filter(
        (ft) =>
          !allowedTypesSet || (ft.latestVersion?.type && allowedTypesSet.has(ft.latestVersion.type))
      ),
    [allowedTypesSet, result.data?.fees]
  )

  return {
    ...omit(result, 'data'),
    feeTemplates,
  }
}

const GetFeesPage = gql`
  query GetFeesPageQuery($input: GetFeesQueryInput, $feeScheduleId: Int!) {
    feesPage(input: $input) {
      data {
        ...FeeCellFeeFragment
      }
      count
    }
  }
  fragment FeeCellFeeFragment on Fee {
    id
    name
    description
    parentFees(feeScheduleId: $feeScheduleId) {
      id
    }
    childFees(feeScheduleId: $feeScheduleId) {
      id
      name
    }
    presentInFeeSchedules {
      id
      name
    }
    latestVersion(feeScheduleId: $feeScheduleId) {
      id
      type
      amount
      amountExact
      minimumAmount
      maximumAmount
      calculatedOn
      valuationMultiplier
      additionalAmountCents
      staffExplanation
      generalLedgerNumber {
        ...GeneralLedgerNumberFragment
      }
      dropdownOptions {
        inputString
        outputCents
      }
      rangeBrackets {
        start
        end
        baseOutputCents
        interval
        extraOutputPerIntervalCents
      }
      exactRangeBrackets {
        start
        end
        baseOutput
        interval
        extraOutputPerInterval
      }
      extraOutputPerIntervalMode
      extraOutputPerIntervalRoundingMode
      interval
      prorateByMonth

      lateFeeAccrualPercent
      lateFeeAccrualPeriod
      lateFeeBasePercent
    }
    createdAt
    createdByUser {
      ...UserDataFragment
    }
    usedInRecordTemplates {
      id
      name
    }
  }
`
export const useFeesPageQuery = (input: GetFeesQueryInput, feeScheduleId: number) => {
  return useQuery<GetFeesPageQuery, GetFeesPageQueryVariables>({
    queryKey: [QueryKey.FeeTemplatesPage, input],
    queryDocument: GetFeesPage,
    variables: {
      input,
      feeScheduleId,
    },
    placeholderData: keepPreviousData,
    enabled: !!input.organizationId,
  })
}

const GetLateFeeSampleCalculation = gql`
  query GetLateFeeSampleCalculationQuery($input: SampleLateFeeCalculatorInput!) {
    calculateSampleLateFees(input: $input) {
      fees {
        cents
      }
    }
  }
`
export const useLateFeeSampleCalculationQuery = (
  input: NullablePartial<SampleLateFeeCalculatorInput>
) => {
  return useQuery<GetLateFeeSampleCalculationQuery, GetLateFeeSampleCalculationQueryVariables>({
    queryKey: [QueryKey.CalculateSampleLateFees, input],
    queryDocument: GetLateFeeSampleCalculation,
    variables: {
      input: {
        currentDate: input.currentDate ?? new Date().toISOString(),
        dueDate: input.dueDate ?? new Date().toISOString(),
        lateFeeInput: input.lateFeeInput ?? ({} as CreateFeeInput),
        totalBeforeLateFeeCents: input.totalBeforeLateFeeCents ?? 0,
      },
    },
    enabled:
      !!input.currentDate &&
      !!input.dueDate &&
      !!input.lateFeeInput &&
      !isNil(input.totalBeforeLateFeeCents),
    enableCancellation: true,
  })
}

const Duplicate = gql`
  mutation DuplicateFeeMutation($id: Int!, $feeScheduleId: Int!) {
    duplicateFee(id: $id, feeScheduleId: $feeScheduleId) {
      id
    }
  }
`

export const useDuplicateFeeMutation = () => {
  return useMutation<DuplicateFeeMutation, DuplicateFeeMutationVariables>({
    mutationDocument: Duplicate,
  })
}

const GetById = gql`
  query GetFeeQuery($id: Int!) {
    fee(id: $id) {
      ...FeeFragment
    }
  }
`

export const useFeeQuery = (
  id: number | undefined,
  options?: UseQueryOptions<GetFeeQuery, GetFeeQueryVariables>
) => {
  const { data, ...rest } = useQuery<GetFeeQuery, GetFeeQueryVariables>({
    queryKey: [QueryKey.Fee, id],
    queryDocument: GetById,
    variables: { id: id ?? 0 },
    ...options,
    enabled: (isNil(options?.enabled) || options.enabled) && !!id,
  })

  const fee = useMemo(() => data?.fee, [data?.fee])

  return {
    ...rest,
    fee,
  }
}

const GetFeeCalculationExplanation = gql`
  mutation GetFeeCalculationExplanationMutation($input: GetFeeCalculationExplanationInput!) {
    getFeeCalculationExplanation(input: $input)
  }
`

export const useGetFeeCalculationExplanationMutation = () => {
  return useMutation<
    GetFeeCalculationExplanationMutation,
    GetFeeCalculationExplanationMutationVariables
  >({
    mutationDocument: GetFeeCalculationExplanation,
  })
}

const GetFeeTemplateForConfig = gql`
  query GetFeeTemplateForConfigQuery($id: Int!, $feeScheduleId: Int!) {
    fee(id: $id) {
      ...ConfigureFeeTemplateFragment
    }
  }
  fragment ConfigureFeeTemplateFragment on Fee {
    id
    name
    description
    parentFees(feeScheduleId: $feeScheduleId) {
      id
    }
    childFees(feeScheduleId: $feeScheduleId) {
      id
      name
    }
    latestVersion(feeScheduleId: $feeScheduleId) {
      id
      type
      amount
      amountExact
      minimumAmount
      maximumAmount
      calculatedOn
      valuationMultiplier
      additionalAmountCents
      staffExplanation
      generalLedgerNumber {
        ...GeneralLedgerNumberFragment
      }
      dropdownOptions {
        inputString
        outputCents
      }
      rangeBrackets {
        start
        end
        baseOutputCents
        interval
        extraOutputPerIntervalCents
      }
      exactRangeBrackets {
        start
        end
        baseOutput
        interval
        extraOutputPerInterval
      }
      extraOutputPerIntervalMode
      extraOutputPerIntervalRoundingMode
      interval
      prorateByMonth

      lateFeeAccrualPercent
      lateFeeAccrualPeriod
      lateFeeBasePercent
    }
    createdAt
    createdByUser {
      ...UserDataFragment
    }
    usedInRecordTemplates {
      id
      name
    }
  }
`
export const useGetFeeTemplateForConfigQuery = (
  id: number | null | undefined,
  feeScheduleId: number
) => {
  const { data, ...rest } = useQuery<
    GetFeeTemplateForConfigQuery,
    GetFeeTemplateForConfigQueryVariables
  >({
    queryKey: [QueryKey.FeeTemplateForConfig, id],
    queryDocument: GetFeeTemplateForConfig,
    variables: { id: id ?? 0, feeScheduleId },
    enabled: !!id,
    gcTime: 0,
    staleTime: 0,
  })
  const feeTemplate = useMemo(() => data?.fee, [data?.fee])
  return {
    ...rest,
    feeTemplate,
  }
}

const GetFeeTag = gql`
  query GetFeeTagQuery($id: Int!, $organizationId: Int!) {
    fee(id: $id) {
      id
      name
    }
    organization(id: $organizationId) {
      id
      currentFeeSchedule {
        id
      }
    }
  }
`
export const useGetFeeTagQuery = (id: number) => {
  const organizationId = useActiveOrgId()
  const { data, ...rest } = useQuery<GetFeeTagQuery, GetFeeTagQueryVariables>({
    queryKey: [QueryKey.FeeTag, id, organizationId],
    queryDocument: GetFeeTag,
    variables: { id, organizationId: organizationId ?? -1 },
    enabled: !!organizationId,
  })
  const fee = useMemo(() => data?.fee, [data?.fee])
  const currentFeeSchedule = useMemo(
    () => data?.organization?.currentFeeSchedule,
    [data?.organization?.currentFeeSchedule]
  )
  return {
    ...rest,
    fee,
    currentFeeSchedule,
  }
}
